/**
 * Центр уведомлений и чаты поддержки — данные с сервера (22.10.2026).
 *
 * Хранятся только на сервере: переписку с поддержкой и рассылки в снимок
 * состояния не кладём, это не данные о деньгах. Центр перечитывается при
 * запуске, при возвращении в приложение и раз в две минуты, пока оно открыто.
 */
import { useCallback, useEffect, useState } from 'react';
import { api, unwrap } from './sync.js';
import { appContext } from './track.js';

export type NoteKind = 'device' | 'pro_renew' | 'pay_failed' | 'trial' | 'news' | 'offer' | 'feature';
/** Что уведомление открывает в приложении. */
export type NoteAction = 'pro' | 'subscription' | 'profile';
export interface Note { id: string; kind: NoteKind; title: string; body: string; action: NoteAction | null; at: string; read: boolean }

export type Topic = 'question' | 'bug' | 'idea' | 'payment';
/**
 * Состояние обращения (22.10.2026): новое → в работе → ответили и ждём
 * человека → закрыто; «исправлено» — закрыто исправлением ошибки.
 */
export type ThreadStatus = 'open' | 'progress' | 'answered' | 'closed' | 'fixed';
export interface ChatRow { id: string; number: number; topic: Topic; title: string; status: ThreadStatus; at: string; unread: number }
/** `auto` — сообщение поддержки, написанное приложением: автоответ, «в работе», «исправили». */
export interface ChatMessage { id: string; author: 'user' | 'support'; body: string; image: boolean; auto: boolean; at: string }
export interface Inbox { notes: Note[]; chats: ChatRow[]; unread: { notes: number; chats: number } }

export const TOPIC_TITLE: Record<Topic, string> = { question: 'Вопрос', bug: 'Ошибка', idea: 'Идея', payment: 'Оплата' };

export async function loadInbox(): Promise<Inbox> {
  return await unwrap(await api('/inbox')) as Inbox;
}

export async function markNotesRead(ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  await unwrap(await api('/inbox/read', { method: 'POST', body: JSON.stringify({ ids }) }));
}

const context = () => {
  const c = appContext();
  return { version: c.version, pwa: c.standalone };
};

/** Новое обращение: возвращает номер чата. */
export async function startThread(input: { topic: Topic; text: string; image: string | null }): Promise<string> {
  const body = await unwrap(await api('/support/thread', {
    method: 'POST', body: JSON.stringify({ ...input, ...context() }),
  })) as { id: string };
  return body.id;
}

export async function loadThread(id: string): Promise<{ thread: ChatRow; messages: ChatMessage[] }> {
  return await unwrap(await api(`/support/thread?id=${encodeURIComponent(id)}`)) as { thread: ChatRow; messages: ChatMessage[] };
}

export async function sendMessage(thread: string, text: string, image: string | null): Promise<void> {
  await unwrap(await api('/support/message', { method: 'POST', body: JSON.stringify({ thread, text, image }) }));
}

/** Снимок из сообщения — тем же путём, с той же сессией. */
export const imageUrl = (messageId: string): string => `/api/support/image?id=${encodeURIComponent(messageId)}`;

export { shrinkImage } from './image-shrink.js';

/** Центр уведомлений: данные и сколько нового — для колокольчика. */
export function useInbox(signedIn: boolean): { inbox: Inbox | null; refresh: () => void } {
  const [inbox, setInbox] = useState<Inbox | null>(null);
  const refresh = useCallback(() => {
    if (!signedIn) { setInbox(null); return; }
    loadInbox().then(setInbox).catch(() => { /* нет сети — покажем, что было */ });
  }, [signedIn]);
  useEffect(() => {
    refresh();
    if (!signedIn) return;
    const onShow = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onShow);
    const timer = window.setInterval(onShow, 120_000);
    return () => { document.removeEventListener('visibilitychange', onShow); window.clearInterval(timer); };
  }, [signedIn, refresh]);
  return { inbox, refresh };
}

/** «12:40», «вчера», «14 окт». */
export function whenText(at: string, now = new Date()): string {
  const d = new Date(at);
  const day = (x: Date) => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
  if (day(d) === day(now)) return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  const y = new Date(now); y.setDate(y.getDate() - 1);
  if (day(d) === day(y)) return 'вчера';
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }).replace('.', '');
}
